import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ApiService from './ApiService';

const CATEGORY_LABELS = {
  'active': 'Active',
  'ending-soon': 'Ending Soon',
  'ended': 'Ended',
  'open-ended': 'Open-ended (no end date)'
};

// Dashboard tiles link here with ?category=active|ending-soon|ended|open-ended.
// Lists every protocol in that category across the org + who it's assigned to.
const ProtocolsOverviewPage = ({ config }) => {
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category') || 'active';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const t = config?.terminology || {};
  const clientTerm = (t.client && t.client.singular) || 'Patient';
  const protocolTerm = (t.protocol && t.protocol.plural) || 'Protocols';

  useEffect(() => {
    let alive = true;
    setLoading(true);
    ApiService.getProtocolsByCategory(category)
      .then((res) => { if (alive) setRows(res.protocols || []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [category]);

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <div className="min-h-screen bg-porcelain">
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div>
          <Link to="/" className="text-sm text-spruce-600 hover:underline">&larr; Dashboard</Link>
          <h1 className="text-2xl font-display font-bold text-ink mt-1">
            {CATEGORY_LABELS[category] || protocolTerm} {protocolTerm}
          </h1>
          <p className="text-ink-soft mt-1">
            {loading ? 'Loading…' : `${rows.length} ${rows.length === 1 ? 'protocol' : 'protocols'}`}
          </p>
        </div>

        <div className="bg-white border border-line rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-ink-soft">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-ink-soft">No {protocolTerm.toLowerCase()} in this category.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-mist text-ink-soft">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Protocol</th>
                    <th className="text-left px-4 py-3 font-medium">{clientTerm}</th>
                    <th className="text-left px-4 py-3 font-medium">Type</th>
                    <th className="text-left px-4 py-3 font-medium">Started</th>
                    <th className="text-left px-4 py-3 font-medium">End By</th>
                    <th className="text-left px-4 py-3 font-medium">Ended</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {rows.map((p) => (
                    <tr key={p.patientProtocolId} className="hover:bg-surface">
                      <td className="px-4 py-3">
                        <Link
                          to={`/patients/${p.patientId}?tab=protocols&highlight=${p.patientProtocolId}`}
                          className="font-medium text-spruce-600 hover:underline"
                        >
                          {p.protocolName || p.medicationName}
                        </Link>
                        {p.reviewDue && (
                          <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full border border-copper-400 text-copper-700">
                            Review due (6mo+)
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/patients/${p.patientId}`} className="text-spruce-600 hover:underline">
                          {p.patientName}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-ink-soft capitalize">{p.protocolType || '—'}</td>
                      <td className="px-4 py-3 font-data text-ink-soft">{fmtDate(p.startDate)}</td>
                      <td className="px-4 py-3 font-data text-ink-soft">{p.endDate ? fmtDate(p.endDate) : 'Ongoing'}</td>
                      <td className="px-4 py-3 font-data text-ink-soft">{fmtDate(p.completedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProtocolsOverviewPage;
